/**
 * Types du domaine chantier.
 */
import type { ClientSummary } from "../quotes/types.js";
import type { InvoiceStatus, InvoiceType } from "../invoices/types.js";
import { round2 } from "../../money.js";
import { subcontractCost } from "../subcontracting/types.js";
import type { ProjectSubcontract, SubcontractCostRef } from "../subcontracting/types.js";
import type { SubcontractInvoice, SubcontractPayment } from "../subcontracting/invoices.js";
import type { MaterialOrderStatus } from "../purchaseOrders/types.js";

export type ProjectStatus =
  | "planned"
  | "permit_pending"
  | "in_progress"
  | "paused"
  | "stopped"
  | "completed"
  | "invoiced"
  | "closed";

/**
 * Les chantiers où du travail est encore attendu.
 *
 * Écrite une fois ici : la même liste était recopiée dans six fichiers, et
 * `stopped` aurait manqué à celui qu'on oublie — un chantier à l'arrêt serait
 * sorti du pilotage le jour même où il faut le surveiller.
 */
export const OPEN_PROJECT_STATUSES: readonly ProjectStatus[] = [
  "planned",
  "permit_pending",
  "in_progress",
  "paused",
  "stopped",
];

/**
 * Les deux interruptions, et ce qui les distingue.
 *
 *   · **en pause** — voulue et bornée : congés, séchage d'une chape, attente
 *     d'une livraison ou d'un autre corps d'état. On sait quand on reprend ;
 *   · **à l'arrêt** — subie, sans date de reprise : intempéries qui durent,
 *     litige, impayé, sinistre, arrêt demandé par le client ou l'inspection.
 *
 * La différence compte pour la suite : un chantier à l'arrêt se signale tant
 * qu'il le reste, une pause non. Et les deux portent un motif — c'est la
 * première question qu'on pose en ouvrant la fiche.
 */
export const INTERRUPTED_PROJECT_STATUSES: readonly ProjectStatus[] = ["paused", "stopped"];

export type PhaseStatus = "pending" | "in_progress" | "completed" | "blocked";

export interface ProjectPhase {
  id: string;
  name: string;
  sortOrder: number;
  progressPercent: number;
  status: PhaseStatus;
  plannedStart: string | null;
  plannedEnd: string | null;
  /**
   * Le lot (corps de métier) de la phase. Nul ou absent pour une phase sans
   * lot — le cas de tout chantier qui ne touche qu'un métier.
   */
  lotId?: string | null;
}

/**
 * Un lot du chantier : un corps de métier et ses phases. Voir
 * `20261030230000_project_lots`.
 */
export interface ProjectLot {
  id: string;
  name: string;
  /** L'ordre du chantier, du premier lot au dernier. */
  sortOrder: number;
  notes: string | null;
}

/**
 * Une phase qui en attend une autre : elle démarre après sa fin, plus un
 * délai en jours calendaires — le séchage d'une chape ne s'arrête pas le
 * week-end.
 */
export interface PhaseLink {
  phaseId: string;
  dependsOnId: string;
  lagDays: number;
}

/**
 * L'ordre du chantier : les phases sans lot d'abord, puis lot par lot, et
 * dans chaque lot l'ordre des phases. Écrit une fois ici, parce que l'écran,
 * la surveillance et le téléphone de l'ouvrier doivent dire la même chose de
 * « la phase suivante ».
 */
export function orderPhases<P extends Pick<ProjectPhase, "sortOrder" | "name" | "lotId">>(
  phases: readonly P[],
  lots: readonly Pick<ProjectLot, "id" | "sortOrder" | "name">[] = [],
): P[] {
  const rang = new Map(lots.map((l) => [l.id, l]));
  const cle = (p: P) => {
    const lot = p.lotId ? rang.get(p.lotId) : undefined;
    return lot ? [1, lot.sortOrder, lot.name] as const : ([0, 0, ""] as const);
  };
  return [...phases].sort((a, b) => {
    const [ga, oa, na] = cle(a);
    const [gb, ob, nb] = cle(b);
    return ga - gb || oa - ob || na.localeCompare(nb, "fr") || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "fr");
  });
}

/**
 * Le nom d'une phase tel qu'on le dit sur un chantier tous corps d'état :
 * « Plomberie · Évacuations ». Seul, « Évacuations » ne dit pas à qui c'est.
 */
export function phaseLabel(
  phase: Pick<ProjectPhase, "name" | "lotId">,
  lots: readonly Pick<ProjectLot, "id" | "name">[] = [],
): string {
  const lot = phase.lotId ? lots.find((l) => l.id === phase.lotId) : undefined;
  return lot ? `${lot.name} · ${phase.name}` : phase.name;
}

export interface MaterialOrderRef {
  id: string;
  status: MaterialOrderStatus;
  totalHt: number;
  neededBy: string | null;
}

export interface TaskRef {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate: string | null;
}

export interface ProjectSummary {
  id: string;
  name: string;
  status: ProjectStatus;
  /** Le motif d'une pause ou d'un arrêt. Nul pour tout autre statut. */
  statusReason: string | null;
  /** Le dernier changement de statut, posé par la base. */
  statusChangedAt: string | null;
  client: ClientSummary | null;
  siteAddress: string | null;
  sitePostalCode: string | null;
  siteCity: string | null;
  siteGps: { lat: number; lng: number } | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  budgetHt: number | null;
  spentHt: number;
  health: ProjectHealth;
  /**
   * Les phases, dans l'ordre du chantier. Portées par la liste et non plus par
   * la seule fiche : la surveillance des délais (`siteAlerts`) regarde tous les
   * chantiers à la fois, et la liste lisait déjà les phases pour l'avancement —
   * sans leurs dates.
   */
  phases: ProjectPhase[];
  /** Les lots, dans l'ordre du chantier. Vide pour un chantier sans lot. */
  lots?: ProjectLot[];
}

export interface ProjectDetail extends ProjectSummary {
  description: string | null;
  notes: string | null;
  orders: MaterialOrderRef[];
  tasks: TaskRef[];
  lots: ProjectLot[];
  /** Les enchaînements entre phases du chantier. */
  links: PhaseLink[];
  /** Ce qui est confié à des sous-traitants. Vide pour le terrain. */
  subcontracts: ProjectSubcontract[];
  /** Leurs factures et ce qu'on leur a réglé. Vides pour le terrain. */
  subcontractInvoices: SubcontractInvoice[];
  subcontractPayments: SubcontractPayment[];
}

/**
 * Une facture rattachée au chantier, vue sous l'angle du chiffre d'affaires.
 *
 * `quoteId` n'est pas décoratif : c'est lui qui permet de ne pas compter deux
 * fois le même travail. Voir `computeInvoicedHt`.
 */
export interface ProjectInvoiceRef {
  id: string;
  quoteId: string | null;
  type: InvoiceType;
  status: InvoiceStatus;
  totalHt: number;
}

/**
 * État financier et d'avancement d'un chantier.
 *
 * Trois questions distinctes, longtemps confondues en une seule barre :
 *
 *   · « où en sont les travaux ? »        — moyenne des phases
 *   · « est-ce que ça tient l'enveloppe ? » — dépensé / budget
 *   · « est-ce que ça rapporte ? »         — facturé − dépensé
 *
 * « Dépensé » compte la sous-traitance **à l'avancement** de ce qui est
 * confié (`subcontractCost`) : un marché signé n'est pas encore une dépense,
 * mais un lot sous-traité fini à moitié en est une pour moitié.
 *
 * La troisième est la seule qui soit une **marge**. Le champ qui portait ce
 * nom valait `enveloppe − dépensé`, ce qui est un reste de budget : le budget
 * est un objectif interne qu'on saisit à la main — le formulaire le nomme
 * d'ailleurs « objectif ou enveloppe » — et non ce que le client paie. Un
 * chantier peut tenir son enveloppe et perdre de l'argent, si l'enveloppe
 * était plus haute que le prix vendu.
 */
export interface ProjectHealth {
  /** Part de l'enveloppe consommée. Peut dépasser 100 % : c'est le signal. */
  budgetUsagePercent: number | null;
  /** Ce qui reste sur l'enveloppe. Ce n'est pas une marge. */
  budgetRemaining: number | null;

  /** Avancement réel, moyenné sur les phases. Null si aucune phase saisie. */
  progressPercent: number | null;

  /** La sous-traitance engagée : le total des marchés, annulés exclus. */
  subcontractedHt: number;
  /** Sa part réalisée, à l'avancement — comptée dans le budget et la marge. */
  subcontractRealisedHt: number;
  /** Dépensé + sous-traitance réalisée : ce que le chantier a coûté. */
  costHt: number;

  /** Facturé au client, hors taxes. Null tant que rien n'est émis. */
  invoicedHt: number | null;
  /** Facturé − coût. Négatif = le chantier perd de l'argent. */
  marginAmount: number | null;
  /** Marge rapportée au facturé, en points. */
  marginPercent: number | null;

  /** Vrai quand on consomme l'enveloppe plus vite qu'on n'avance les travaux. */
  overspending: boolean;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Marge de tolérance avant de crier à la dérive, en points de pourcentage. */
const DRIFT_TOLERANCE = 10;

/** Une facture qui n'engage encore rien, ou qui a été annulée. */
const NON_EMISES = new Set<InvoiceStatus>(["draft", "cancelled"]);

/**
 * Le chiffre d'affaires hors taxes d'un chantier.
 *
 * Le piège que cette fonction existe pour éviter : `create-invoice-from-quote`
 * produit un **acompte** portant une *fraction* du devis, et une facture
 * **finale** portant son *total*. Sommer les deux compterait le même travail
 * deux fois — un chantier facturé 30 % d'acompte puis soldé afficherait
 * 130 % du devis, et donc une marge inventée de toutes pièces.
 *
 * La règle, par devis : dès qu'une facture finale existe, elle fait foi et les
 * acomptes et situations du même devis sont ignorés ; sinon on additionne les
 * partiels. Les avoirs, dont le montant est déjà négatif en base, s'ajoutent
 * dans tous les cas.
 *
 * Les factures sans devis rattaché sont additionnées telles quelles : rien ne
 * permet de les regrouper, et rien ne suggère qu'elles se recouvrent.
 */
export function computeInvoicedHt(
  invoices: readonly ProjectInvoiceRef[],
): number | null {
  const emises = invoices.filter((i) => !NON_EMISES.has(i.status));
  if (emises.length === 0) return null;

  let total = 0;
  const parDevis = new Map<string, ProjectInvoiceRef[]>();

  for (const facture of emises) {
    if (facture.type === "credit_note" || facture.quoteId == null) {
      total += facture.totalHt;
      continue;
    }
    const groupe = parDevis.get(facture.quoteId) ?? [];
    groupe.push(facture);
    parDevis.set(facture.quoteId, groupe);
  }

  for (const groupe of parDevis.values()) {
    const finales = groupe.filter((f) => f.type === "invoice");
    const retenues = finales.length > 0 ? finales : groupe;
    for (const f of retenues) total += f.totalHt;
  }

  return round2(total);
}

export function computeProjectHealth(
  budgetHt: number | null,
  spentHt: number,
  phases: readonly (Pick<ProjectPhase, "progressPercent"> & Partial<Pick<ProjectPhase, "id" | "lotId">>)[] = [],
  invoices: readonly ProjectInvoiceRef[] = [],
  subcontracts: readonly SubcontractCostRef[] = [],
): ProjectHealth {
  const sousTraitance = subcontractCost(
    subcontracts,
    phases.map((p) => ({ id: p.id ?? "", lotId: p.lotId ?? null, progressPercent: p.progressPercent })),
  );
  const costHt = round2(spentHt + sousTraitance.realisedHt);

  const budgetUsagePercent =
    budgetHt && budgetHt > 0 ? round1((costHt / budgetHt) * 100) : null;
  const budgetRemaining = budgetHt == null ? null : round2(budgetHt - costHt);

  const progressPercent = phases.length
    ? round1(phases.reduce((sum, p) => sum + p.progressPercent, 0) / phases.length)
    : null;

  const invoicedHt = computeInvoicedHt(invoices);
  const marginAmount = invoicedHt == null ? null : round2(invoicedHt - costHt);
  const marginPercent =
    invoicedHt != null && invoicedHt > 0
      ? round1(((invoicedHt - costHt) / invoicedHt) * 100)
      : null;

  // Sans phases saisies, on ne peut rien dire de la dérive. Mieux vaut se
  // taire qu'alerter sur un chantier qui va bien : une alerte qui se trompe
  // souvent finit par ne plus être lue.
  const overspending =
    budgetUsagePercent != null &&
    progressPercent != null &&
    budgetUsagePercent > progressPercent + DRIFT_TOLERANCE;

  return {
    budgetUsagePercent,
    budgetRemaining,
    progressPercent,
    subcontractedHt: sousTraitance.committedHt,
    subcontractRealisedHt: sousTraitance.realisedHt,
    costHt,
    invoicedHt,
    marginAmount,
    marginPercent,
    overspending,
  };
}

/**
 * Un chantier qui mérite l'attention du patron.
 *
 * Trois signaux, et non un seul : perdre de l'argent (facturé < dépensé),
 * dépasser son enveloppe, ou consommer plus vite qu'on n'avance. Ce sont des
 * faits différents et le patron veut voir les trois — ne retenir que la marge
 * laisserait passer tous les chantiers pas encore facturés, c'est-à-dire ceux
 * qu'on peut encore redresser.
 *
 * Cette règle vivait en deux exemplaires, dans le domaine chantier et dans le
 * tableau de bord. Les deux ont fini par ne plus dire la même chose, ce qui
 * est le sort de toute règle recopiée.
 */
export function isDrifting(health: ProjectHealth): boolean {
  return (
    health.overspending ||
    (health.marginAmount != null && health.marginAmount < 0) ||
    (health.budgetRemaining != null && health.budgetRemaining < 0)
  );
}

/**
 * De quoi classer les dérives, du plus grave au moins grave.
 *
 * La perte réelle quand elle est connue — c'est de l'argent effectivement
 * perdu ; à défaut, le dépassement d'enveloppe, qui n'est qu'un écart à un
 * objectif interne.
 */
export function driftSeverity(health: ProjectHealth): number {
  return health.marginAmount ?? health.budgetRemaining ?? Infinity;
}
